import { transplantExif } from './exif.js';

/**
 * Подготовка снимка к загрузке.
 *
 * Правило 3: ресайз строго ДО хеширования — оригиналом считается то,
 * что вышло из этого пайплайна, и дальше файл неприкосновенен.
 * Без сжатия сто юнитов дают ~3,5 ТБ в год вместо ~350 ГБ.
 */
export const MAX_EDGE_PX = 1600;
export const JPEG_QUALITY = 0.8;

export interface PreparedPhoto {
  bytes: Uint8Array;
  sha256: string;
  width: number;
  height: number;
  exifPreserved: boolean;
}

function targetSize(width: number, height: number): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= MAX_EDGE_PX) return { width, height };
  const scale = MAX_EDGE_PX / longest;
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const copy = new Uint8Array(bytes);
  const digest = await crypto.subtle.digest('SHA-256', copy.buffer);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

export async function preparePhoto(file: Blob): Promise<PreparedPhoto> {
  const original = new Uint8Array(await file.arrayBuffer());

  // createImageBitmap применяет EXIF-ориентацию сам — снимок не ляжет боком.
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  const size = targetSize(bitmap.width, bitmap.height);

  const canvas = document.createElement('canvas');
  canvas.width = size.width;
  canvas.height = size.height;

  const ctx = canvas.getContext('2d');
  if (ctx === null) throw new Error('Не удалось подготовить изображение');
  ctx.drawImage(bitmap, 0, 0, size.width, size.height);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY);
  });
  if (blob === null) throw new Error('Не удалось сжать изображение');

  const resized = new Uint8Array(await blob.arrayBuffer());
  const withExif = transplantExif(original, resized);

  return {
    bytes: withExif,
    sha256: await sha256Hex(withExif),
    width: size.width,
    height: size.height,
    exifPreserved: withExif.length !== resized.length,
  };
}

/** Текущая позиция. Отказ не блокирует осмотр — он становится записью без GPS. */
export async function currentPosition(
  timeoutMs = 8000,
): Promise<{ lat: number; lon: number; accuracyMeters: number } | null> {
  if (!('geolocation' in navigator)) return null;

  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        resolve({
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          accuracyMeters: pos.coords.accuracy,
        }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 30_000 },
    );
  });
}
